import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Psychology as CohereIcon,
  Memory as LlamaIcon,
  RemoveRedEye as VisionIcon,
} from '@mui/icons-material';
import { OciAiTestDto } from '../../dto/OciAiTestDto';

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  models: OciAiTestDto.ModelInfo[];
}

/**
 * AI 모델 선택 컴포넌트
 * 
 * @description 사용 가능한 AI 모델을 선택할 수 있는 드롭다운 컴포넌트
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
export const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel,
  onModelChange,
  models,
}) => {
  // 모델 프로바이더에 따른 아이콘 반환
  const getProviderIcon = (provider: string, type: string) => {
    if (type === 'vision') return <VisionIcon />;
    if (provider === 'cohere') return <CohereIcon />;
    if (provider === 'meta') return <LlamaIcon />;
    return <CohereIcon />;
  };

  // 모델 프로바이더에 따른 색상 반환
  const getProviderColor = (provider: string, type: string): "primary" | "secondary" | "success" | "warning" | "error" | "info" => {
    if (type === 'vision') return 'success';
    if (provider === 'cohere') return 'primary';
    if (provider === 'meta') return 'warning';
    return 'primary';
  };

  // 모델 성능 등급 반환
  const getModelTier = (modelId: string) => {
    if (modelId.includes('405b')) return '최강';
    if (modelId.includes('90b')) return '고급';
    if (modelId.includes('70b')) return '고급';
    if (modelId.includes('plus')) return '최고급';
    if (modelId.includes('r-08')) return '저렴';
    if (modelId.includes('11b')) return '경량';
    return '중급';
  };

  // 선택된 모델 정보 찾기
  const selectedModelInfo = models.find(model => model.id === selectedModel);

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel>AI 모델 선택</InputLabel>
        <Select
          value={selectedModel}
          label="AI 모델 선택"
          onChange={(e) => onModelChange(e.target.value as string)}
        >
          {models.map((model) => (
            <MenuItem key={model.id} value={model.id}>
              <Box sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  {getProviderIcon(model.provider, model.type)}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {model.name}
                  </Typography>
                  <Chip
                    label={getModelTier(model.id)}
                    size="small"
                    color={getProviderColor(model.provider, model.type)}
                    variant="outlined"
                  />
                </Stack>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {model.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 선택된 모델 상세 정보 */}
      {selectedModelInfo && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              {getProviderIcon(selectedModelInfo.provider, selectedModelInfo.type)}
              <Typography variant="subtitle2">
                {selectedModelInfo.name}
              </Typography>
            </Stack>
            
            <Typography variant="body2" color="text.secondary">
              {selectedModelInfo.description}
            </Typography>
            
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`${selectedModelInfo.provider.toUpperCase()}`}
                size="small"
                variant="filled"
                color={getProviderColor(selectedModelInfo.provider, selectedModelInfo.type)}
              />
              <Chip
                label={selectedModelInfo.type === 'vision' ? 'Vision' : 'Text'}
                size="small"
                variant="outlined"
                color={selectedModelInfo.type === 'vision' ? 'success' : 'default'}
              />
              <Tooltip title="Maximum tokens per request">
                <Chip
                  label={`Max: ${selectedModelInfo.maxTokens.toLocaleString()}`}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
};